import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Star, CheckCircle, AlertTriangle, Clock } from 'lucide-react';
import { csatService, type EncuestaPublica } from '@/services/csat';

export function EncuestaPage() {
  const { token } = useParams<{ token: string }>();
  const [encuesta, setEncuesta] = useState<EncuestaPublica | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [puntuacion, setPuntuacion] = useState<number | null>(null);
  const [hoverPuntuacion, setHoverPuntuacion] = useState<number | null>(null);
  const [comentario, setComentario] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [enviado, setEnviado] = useState(false);

  useEffect(() => {
    if (!token) return;
    setLoading(true);
    csatService.obtenerPorToken(token)
      .then(setEncuesta)
      .catch(() => setError('Encuesta no encontrada'))
      .finally(() => setLoading(false));
  }, [token]);

  const handleSubmit = useCallback(async () => {
    if (!token || !puntuacion) return;
    setEnviando(true);
    try {
      const result = await csatService.responder(token, { puntuacion, comentario: comentario || undefined });
      setEncuesta(result);
      setEnviado(true);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Error al enviar respuesta';
      setError(message);
    } finally {
      setEnviando(false);
    }
  }, [token, puntuacion, comentario]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-pulse text-gray-400">Cargando encuesta...</div>
      </div>
    );
  }

  if (error || !encuesta) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-xl shadow-sm border p-8 max-w-md text-center">
          <AlertTriangle className="h-12 w-12 text-amber-500 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Encuesta no disponible</h2>
          <p className="text-sm text-gray-500">{error || 'No se encontró la encuesta solicitada.'}</p>
        </div>
      </div>
    );
  }

  if (encuesta.ya_respondida || enviado) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-xl shadow-sm border p-8 max-w-md text-center">
          <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Gracias por tu respuesta</h2>
          <p className="text-sm text-gray-500">Tu feedback nos ayuda a mejorar nuestro servicio.</p>
          {encuesta.puntuacion && (
            <div className="flex justify-center gap-1 mt-4">
              {[1, 2, 3, 4, 5].map((i) => (
                <Star
                  key={i}
                  className={`h-6 w-6 ${i <= encuesta.puntuacion! ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  if (encuesta.expirada) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-xl shadow-sm border p-8 max-w-md text-center">
          <Clock className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Encuesta expirada</h2>
          <p className="text-sm text-gray-500">El plazo para responder esta encuesta ha finalizado.</p>
        </div>
      </div>
    );
  }

  const labels = ['Muy malo', 'Malo', 'Regular', 'Bueno', 'Excelente'];

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-sm border p-8 max-w-lg w-full">
        <div className="text-center mb-6">
          <h1 className="text-xl font-semibold text-gray-900 mb-1">
            Como fue tu experiencia?
          </h1>
          {encuesta.ticket_numero && (
            <p className="text-sm text-gray-500">
              Ticket #{String(encuesta.ticket_numero).padStart(4, '0')}
              {encuesta.cliente_nombre && ` — ${encuesta.cliente_nombre}`}
            </p>
          )}
        </div>

        {/* Star Rating */}
        <div className="flex justify-center gap-2 mb-2">
          {[1, 2, 3, 4, 5].map((i) => (
            <button
              key={i}
              type="button"
              onClick={() => setPuntuacion(i)}
              onMouseEnter={() => setHoverPuntuacion(i)}
              onMouseLeave={() => setHoverPuntuacion(null)}
              className="p-1 transition-transform hover:scale-110"
            >
              <Star
                className={`h-10 w-10 transition-colors ${
                  i <= (hoverPuntuacion ?? puntuacion ?? 0)
                    ? 'text-yellow-400 fill-yellow-400'
                    : 'text-gray-300'
                }`}
              />
            </button>
          ))}
        </div>

        {puntuacion && (
          <p className="text-center text-sm text-gray-500 mb-6">{labels[puntuacion - 1]}</p>
        )}

        {/* Comment */}
        <div className="mb-6">
          <label htmlFor="comentario" className="block text-sm font-medium text-gray-700 mb-1">
            Comentario (opcional)
          </label>
          <textarea
            id="comentario"
            value={comentario}
            onChange={(e) => setComentario(e.target.value)}
            rows={3}
            maxLength={1000}
            placeholder="Cuentanos mas sobre tu experiencia..."
            className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none resize-none"
          />
        </div>

        {/* Submit */}
        <button
          type="button"
          onClick={handleSubmit}
          disabled={!puntuacion || enviando}
          className="w-full px-4 py-2.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {enviando ? 'Enviando...' : 'Enviar respuesta'}
        </button>
      </div>
    </div>
  );
}
